export default defineAppConfig({
  ui: {
    badge: {
      defaultVariants: {
        color: 'primary',
        size: 'md',
        variant: 'solid',
      },
      slots: {
        base: [
          'font-[family-name:var(--font-display)]',
          'uppercase',
          'tracking-[0.12em]',
          'text-[10px]',
          'rounded-[var(--radius-pill)]',
          'px-2',
          'py-1',
        ].join(' '),
      },
      variants: {
        variant: {
          outline: 'bg-transparent ring-1 ring-inset ring-[color:var(--color-brand-ink)] text-[color:var(--color-brand-ink)]',
          solid: 'bg-[color:var(--color-brand-yellow)] text-[color:var(--color-brand-ink)]',
          subtle: 'bg-[color:var(--color-brand-ink)] text-[color:var(--color-brand-yellow)]',
        },
      },
    },

    button: {
      defaultVariants: {
        color: 'primary',
        size: 'md',
        variant: 'solid',
      },
      slots: {
        base: [
          'font-[family-name:var(--font-display)]',
          'uppercase',
          'tracking-[0.1em]',
          'text-[13px]',
          'rounded-[var(--radius-pill)]',
          'transition-transform',
          'duration-150',
          'ease-out',
          'active:translate-y-[2px]',
          'active:shadow-none',
          'focus-visible:outline-2',
          'focus-visible:outline-offset-2',
          'focus-visible:outline-[color:var(--color-brand-ink)]',
        ].join(' '),
      },
      variants: {
        variant: {
          ghost: [
            'bg-transparent',
            'text-[color:var(--color-brand-ink)]',
            'shadow-none',
            'hover:bg-[color:var(--color-brand-ink)]/5',
          ].join(' '),
          outline: [
            'bg-transparent',
            'text-[color:var(--color-brand-chalk)]',
            'ring-2',
            'ring-[color:var(--color-brand-chalk)]',
            'ring-inset',
            'shadow-none',
            'hover:bg-[color:var(--color-brand-chalk)]/10',
          ].join(' '),
          solid: [
            'bg-[color:var(--color-brand-yellow)]',
            'text-[color:var(--color-brand-ink)]',
            'shadow-[var(--shadow-button)]',
            'hover:bg-[color:var(--color-brand-yellow)]',
          ].join(' '),
        },
      },
    },

    card: {
      defaultVariants: {
        variant: 'outline',
      },
      slots: {
        body: 'p-5',
        footer: 'p-5 border-t-2 border-brand-ink',
        header: 'p-5 border-b-2 border-brand-ink',
        root: [
          'bg-brand-chalk',
          'text-brand-ink',
          'border-2',
          'border-brand-ink',
          'rounded-card',
          'shadow-[0_5px_0_var(--color-brand-ink)]',
          'overflow-hidden',
        ].join(' '),
      },
    },

    colors: {
      error: 'red',
      info: 'ink',
      neutral: 'ink',
      primary: 'yellow',
      secondary: 'red',
      success: 'yellow',
      warning: 'red',
    },

    container: {
      base: 'mx-auto w-full px-4 sm:px-6 lg:px-8 max-w-7xl',
    },

    dropdownMenu: {
      slots: {
        // z-[60] beats the in-game challenge modal veil (z-20) and the
        // GameFab trigger (z-50). The dropdown content portals to body
        // so the trigger's z-index doesn't propagate — without explicit
        // z here, the menu renders under the modal veil during a round.
        //
        // Explicit `bg-white` (instead of relying on `bg-default`)
        // guarantees the popup is opaque white in every theme. The
        // showdown stage applies `color: white` to its descendants for
        // the dark gameplay surface; the dropdown portals out of that
        // tree, but pinning the bg here means it can't accidentally
        // inherit a translucent or themed surface from any future
        // styling layer.
        content: 'z-[60] bg-white text-brand-ink border-2 border-brand-ink rounded-card shadow-[0_4px_0_var(--color-brand-ink)]',
        item: 'data-[highlighted]:bg-brand-ink/8 data-[highlighted]:text-brand-ink rounded-md transition-colors',
        itemLeadingIcon: 'text-brand-ink/60',
        separator: 'bg-brand-ink/10',
      },
    },

    formField: {
      slots: {
        description: 'text-brand-ink/70',
        error: 'mt-2 text-sm font-body text-brand-red',
        help: 'mt-2 text-brand-ink/70',
        hint: 'text-brand-ink/70',
        label: 'block font-display uppercase text-[11px] tracking-[0.16em] text-brand-ink mb-2',
      },
    },

    icons: {
      arrowDown: 'mdi:arrow-down',
      arrowLeft: 'mdi:arrow-left',
      arrowRight: 'mdi:arrow-right',
      arrowUp: 'mdi:arrow-up',
      caution: 'mdi:alert-circle',
      check: 'mdi:check',
      chevronDoubleLeft: 'mdi:chevron-double-left',
      chevronDoubleRight: 'mdi:chevron-double-right',
      chevronDown: 'mdi:chevron-down',
      chevronLeft: 'mdi:chevron-left',
      chevronRight: 'mdi:chevron-right',
      chevronUp: 'mdi:chevron-up',
      close: 'mdi:close',
      copy: 'mdi:content-copy',
      copyCheck: 'mdi:check-all',
      dark: 'mdi:weather-night',
      drag: 'mdi:drag-vertical',
      ellipsis: 'mdi:dots-horizontal',
      error: 'mdi:close-circle',
      external: 'mdi:open-in-new',
      eye: 'mdi:eye',
      eyeOff: 'mdi:eye-off',
      file: 'mdi:file',
      folder: 'mdi:folder',
      folderOpen: 'mdi:folder-open',
      hash: 'mdi:pound',
      info: 'mdi:information',
      light: 'mdi:weather-sunny',
      loading: 'mdi:loading',
      menu: 'mdi:menu',
      minus: 'mdi:minus',
      panelClose: 'mdi:arrow-collapse-left',
      panelOpen: 'mdi:arrow-expand-right',
      plus: 'mdi:plus',
      reload: 'mdi:reload',
      search: 'mdi:magnify',
      stop: 'mdi:stop',
      success: 'mdi:check-circle',
      system: 'mdi:monitor',
      tip: 'mdi:lightbulb',
      upload: 'mdi:upload',
      warning: 'mdi:alert',
    },

    input: {
      defaultVariants: {
        size: 'lg',
        variant: 'none',
      },
      slots: {
        base: [
          'w-full',
          'bg-brand-chalk',
          'text-brand-ink',
          'border-2',
          'border-brand-ink',
          'rounded-pill',
          'font-body',
          'placeholder:text-brand-ink/40',
          'focus:outline-none',
          'focus:border-brand-ink',
          'focus:shadow-[0_4px_0_var(--color-brand-ink)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'transition-shadow',
        ].join(' '),
      },
    },

    modal: {
      slots: {
        body: 'p-6',
        content: [
          'bg-[color:var(--color-brand-ink)]',
          'text-[color:var(--color-brand-chalk)]',
          'rounded-[var(--radius-card)]',
          'shadow-[0_20px_50px_rgba(0,0,0,0.30)]',
          'ring-0',
          'border',
          'border-[color:var(--color-brand-chalk)]/20',
        ].join(' '),
        footer: 'p-6',
        header: 'p-6',
        overlay: 'bg-black/60',
        title: 'font-[family-name:var(--font-editorial)] italic font-black text-2xl text-[color:var(--color-brand-chalk)]',
      },
    },

    navigationMenu: {
      slots: {
        link: [
          'font-[family-name:var(--font-display)]',
          'uppercase',
          'tracking-[0.1em]',
          'text-[13px]',
          'text-[color:var(--color-brand-chalk)]',
          'hover:text-[color:var(--color-brand-yellow)]',
        ].join(' '),
        linkLabelExternalIcon: 'text-[color:var(--color-brand-yellow)]',
        root: 'bg-[color:var(--color-brand-ink)]',
      },
    },

    textarea: {
      defaultVariants: {
        size: 'lg',
        variant: 'none',
      },
      slots: {
        base: [
          'w-full',
          'bg-brand-chalk',
          'text-brand-ink',
          'border-2',
          'border-brand-ink',
          'rounded-pill',
          'font-body',
          'placeholder:text-brand-ink/40',
          'focus:outline-none',
          'focus:border-brand-ink',
          'focus:shadow-[0_4px_0_var(--color-brand-ink)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'transition-shadow',
        ].join(' '),
      },
    },
  },
})
